import React, { useState, useEffect } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import axios from 'axios';
import { API_BASE_URL, counterpartyApi, isinMasterApi, markToMarketApi, repoDealsApi, getAuthHeaders, getCurrentUserId } from '../services/api';
import RepoBlotter from '../components/RepoBlotter';
import DocumentUpload from '../components/DocumentUpload';
import DocumentList from '../components/DocumentList';
import '../styles/shared-forms.css';
import './RepoPage.css';
import { validateAvailableBalance } from '../utils/balanceValidation';
import { checkHolidayForCurrency, formatHolidayMessage } from '../utils/holidayValidation';
import { DATE_OBJ_MIN, DATE_OBJ_MAX } from '../utils/dateBounds';

/** Format a numeric value with thousand separators, preserving decimals. Returns '' for blanks. */
const formatAmountWithSeparators = (value) => {
  if (value === null || value === undefined || value === '') return '';
  const raw = String(value).replace(/,/g, '');
  const num = Number(raw);
  if (!Number.isFinite(num)) return String(value);
  const [intPart, decPart] = raw.split('.');
  const sign = num < 0 ? '-' : '';
  const intDigits = (intPart || '0').replace('-', '');
  const grouped = Number(intDigits).toLocaleString('en-US');
  return decPart !== undefined ? `${sign}${grouped}.${decPart}` : `${sign}${grouped}`;
};

const RepoPage = () => {
  const getCounterpartyOptionValue = (cp) => {
    const uid = String(cp?.unique_id || '').trim();
    if (uid) return uid; // preferred: globally unique (e.g., c7 / i7 / j7)
    if (cp?.id !== undefined && cp?.id !== null && cp?.id !== '') return String(cp.id);
    return '';
  };
  // Check for reinvestment data from maturity handling
  const getReinvestmentData = () => {
    const reinvestmentData = localStorage.getItem('reinvestmentDealData');
    if (reinvestmentData) {
      try {
        const data = JSON.parse(reinvestmentData);
        localStorage.removeItem('reinvestmentDealData'); // Clear after use
        return data;
      } catch (error) {
        console.error('Error parsing reinvestment data:', error);
      }
    }
    return null;
  };

  const reinvestmentData = getReinvestmentData();

  // State for form fields
  const [dealType, setDealType] = useState('Repo');
  const [counterparty, setCounterparty] = useState('');
  const [tradeDate, setTradeDate] = useState(new Date());
  const [valueDate, setValueDate] = useState(new Date());
  const [maturityDate, setMaturityDate] = useState(new Date());
  const [principalAmount, setPrincipalAmount] = useState(reinvestmentData?.principalAmount || '');
  const [rate, setRate] = useState('');
  // Default day basis 365, fund movement Yes (per ops convention).
  // Settlement mode is auto-picked once the modes list loads (Seylan Bank).
  const [calculationDayBasis, setCalculationDayBasis] = useState('365');
  const [selectedIsins, setSelectedIsins] = useState([]); // [{ isin: string, faceValue: number|null }]
  const [haircut, setHaircut] = useState('');
  const [settlementMode, setSettlementMode] = useState('');
  const [fundMovement, setFundMovement] = useState('yes');

  // State for auto-filled fields
  const [interestAmount, setInterestAmount] = useState('0.00');
  const [maturityAmount, setMaturityAmount] = useState('0.00');
  const [tenor, setTenor] = useState(0);
  const [faceValue, setFaceValue] = useState('');
  const [faceValueAdjustment, setFaceValueAdjustment] = useState('');
  const [faceValueAsPerCounterparty, setFaceValueAsPerCounterparty] = useState('');
  const [dirtyPricesByIsin, setDirtyPricesByIsin] = useState({});

  // State for dropdowns
  const [counterparties, setCounterparties] = useState([]);
  const [isins, setIsins] = useState([]);
  const [loadingCounterparties, setLoadingCounterparties] = useState(false);
  const [loadingIsins, setLoadingIsins] = useState(false);
  const [settlementModes, setSettlementModes] = useState([]);
  const [loadingSettlementModes, setLoadingSettlementModes] = useState(false);

  // State for holiday validation errors
  const [tradeDateHolidayError, setTradeDateHolidayError] = useState('');
  const [valueDateHolidayError, setValueDateHolidayError] = useState('');

  // State for form submission
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [saveSuccess, setSaveSuccess] = useState('');
  
  // State for blotter refresh
  const [blotterRefreshTrigger, setBlotterRefreshTrigger] = useState(0);
  const [createdRepoDealId, setCreatedRepoDealId] = useState(null);
  const [showRepoDocumentSection, setShowRepoDocumentSection] = useState(false);
  const [isRepoViewMode, setIsRepoViewMode] = useState(false);
  const [isRepoEditMode, setIsRepoEditMode] = useState(false);
  const [editingRepoDealId, setEditingRepoDealId] = useState(null);
  const [editingRepoDealStatus, setEditingRepoDealStatus] = useState('');
  const [selectedRepoDocumentDealId, setSelectedRepoDocumentDealId] = useState(null);
  
  // State for GSEC report data and balance validation
  const [gsecReportData, setGsecReportData] = useState([]);
  const [balanceValidation, setBalanceValidation] = useState({
    isValid: true,
    message: ''
  });

  // Deal type options
  const dealTypeOptions = ['Repo', 'Reverse Repo'];

  // Day basis options
  const dayBasisOptions = ['364', '365'];

  // Fetch counterparties on component mount
  useEffect(() => {
    fetchCounterparties();
  }, []);

  // Fetch ISINs on component mount
  useEffect(() => {
    fetchIsins();
  }, []);

  // Fetch settlement modes on component mount
  useEffect(() => {
    fetchSettlementModes();
  }, []);

  // Auto-pick "Seylan Bank" settlement mode once the list loads.
  // Only sets the value if the user hasn't picked one yet.
  useEffect(() => {
    if (!settlementModes || settlementModes.length === 0) return;
    if (settlementMode) return;
    const containsSeylanBank = (m) => {
      const haystack = [
        m.paymentMethodOwner,
        m.paymentMethod,
        m.bankPaymentCode
      ].map((v) => String(v || '').toLowerCase()).join(' ');
      return haystack.includes('seylan bank');
    };
    const pick = settlementModes.find(containsSeylanBank);
    if (pick && pick.bankPaymentCode) {
      setSettlementMode(pick.bankPaymentCode);
    }
  }, [settlementModes, settlementMode]);

  // Fetch GSEC report data for balance validation
  const fetchGsecReportData = async () => {
    try {
      const asAtDate = new Date().toISOString().split('T')[0];
      const response = await axios.get(`${API_BASE_URL}/reports/gsec`, { params: { asAtDate } });
      const data = response.data?.data || [];
      setGsecReportData(data);
      return data;
    } catch (err) {
      console.error('Failed to fetch GSEC report data:', err);
      setGsecReportData([]);
      return [];
    }
  };

  // Fetch GSEC report data on component mount
  useEffect(() => {
    fetchGsecReportData();
  }, []);

  // Calculate tenor whenever value date or maturity date changes
  useEffect(() => {
    if (valueDate && maturityDate) {
      const daysDiff = Math.round((maturityDate - valueDate) / (1000 * 60 * 60 * 24));
      setTenor(Math.max(0, daysDiff));
    }
  }, [valueDate, maturityDate]);

  // Calculate interest amount and maturity amount whenever principal, rate, tenor, or day basis changes
  useEffect(() => {
    if (principalAmount && rate && tenor && calculationDayBasis) {
      const principal = parseFloat(principalAmount);
      const ratePercent = parseFloat(rate);
      const dayBasis = parseInt(calculationDayBasis);
      
      if (!isNaN(principal) && !isNaN(ratePercent) && !isNaN(dayBasis) && dayBasis > 0) {
        // Interest calculation: (Principal * Rate * Tenor) / Day Basis
        const interest = (principal * ratePercent * tenor) / (100 * dayBasis);
        setInterestAmount(interest.toFixed(4));
        setMaturityAmount((principal + interest).toFixed(4));
      }
    }
  }, [principalAmount, rate, tenor, calculationDayBasis]);

  // Real-time balance validation (only required for Repo, where Sherwood
  // borrows and pledges its own securities as collateral).
  // We validate each selected ISIN against its OWN allocation - not the total
  // required face value against a single ISIN - because the user can attach
  // multiple ISINs as collateral, each with its own face-value allocation.
  useEffect(() => {
    if (dealType !== 'Repo' || gsecReportData.length === 0 || selectedIsins.length === 0) {
      setBalanceValidation({ isValid: true, message: '' });
      return;
    }

    // Only validate lines that have an actual allocation entered.
    const linesWithAllocation = selectedIsins.filter(
      (line) => parseFloat(line.faceValue) > 0
    );
    if (linesWithAllocation.length === 0) {
      setBalanceValidation({ isValid: true, message: '' });
      return;
    }

    const failures = [];
    for (const line of linesWithAllocation) {
      const allocation = parseFloat(line.faceValue) || 0;
      const result = validateAvailableBalance(
        line.isin,
        allocation,
        dealType,
        gsecReportData
      );
      if (!result.isValid) {
        failures.push({ isin: line.isin, ...result });
      }
    }

    if (failures.length === 0) {
      setBalanceValidation({ isValid: true, message: 'All ISIN allocations within available balance.' });
    } else {
      const summary = failures
        .map((f) => `${f.isin}: available ${Number(f.availableBalance || 0).toLocaleString()}, requested ${Number(f.requestedAmount || 0).toLocaleString()}`)
        .join(' | ');
      setBalanceValidation({
        isValid: false,
        message: `Insufficient available balance for ${failures.length} ISIN(s): ${summary}`,
        failures,
      });
    }
  }, [dealType, selectedIsins, gsecReportData]);

  // Calculate face value based on haircut and dirty price whenever maturity amount, haircut, or dirty prices change
  useEffect(() => {
    if (maturityAmount && haircut) {
      const maturity = parseFloat(maturityAmount);
      const haircutPercent = parseFloat(haircut);
      
      if (!isNaN(maturity) && !isNaN(haircutPercent) && haircutPercent >= 0 && haircutPercent < 100) {
        // Basic Face Value = (Maturity Amount × 100) ÷ (100 - Haircut)
        const basicFaceValue = (maturity * 100) / (100 - haircutPercent);
        
        // If dirty price is available from first selected ISIN, apply the new formula
        const firstIsin = selectedIsins.length > 0 ? selectedIsins[0].isin : null;
        const dirtyPrice = firstIsin ? dirtyPricesByIsin[firstIsin] : null;
        
        if (dirtyPrice && dirtyPrice > 0) {
          // Face Value = Basic Face Value × 100 ÷ Dirty Price
          const adjustedFaceValue = (basicFaceValue * 100) / dirtyPrice;
          setFaceValue(adjustedFaceValue.toFixed(2));
        } else {
          // Use basic face value if no dirty price available
          setFaceValue(basicFaceValue.toFixed(2));
        }
      }
    }
  }, [maturityAmount, haircut, selectedIsins, dirtyPricesByIsin]);

  // Calculate face value as per counterparty whenever face value or adjustment changes
  useEffect(() => {
    if (faceValue) {
      const baseFaceValue = parseFloat(faceValue);
      const adjustment = parseFloat(faceValueAdjustment) || 0;
      
      if (!isNaN(baseFaceValue)) {
        const adjustedValue = baseFaceValue + adjustment;
        setFaceValueAsPerCounterparty(adjustedValue.toFixed(2));
      }
    }
  }, [faceValue, faceValueAdjustment]);

  // Fetch counterparties from API
  const fetchCounterparties = async () => {
    setLoadingCounterparties(true);
    try {
      const counterpartyData = await counterpartyApi.getAll();
      setCounterparties(counterpartyData);
    } catch (error) {
      console.error('Error fetching counterparties:', error);
      setCounterparties([]);
    } finally {
      setLoadingCounterparties(false);
    }
  };

  // Fetch ISINs from API
  const fetchIsins = async () => {
    setLoadingIsins(true);
    try {
      const isinData = await isinMasterApi.getAll();
      setIsins(isinData);
    } catch (error) {
      console.error('Error fetching ISINs:', error);
      setIsins([]);
    } finally {
      setLoadingIsins(false);
    }
  };

  const fetchSettlementModes = async () => {
    setLoadingSettlementModes(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/payment-master`);
      const items = response.data?.data || response.data || [];

      const mapped = (Array.isArray(items) ? items : []).map((m) => {
        const bankPaymentCode = m.bank_payment_code || m.bankPaymentCode || '';
        const paymentMethod = m.payment_method || m.method || m.PaymentMethod || '';
        const paymentMethodOwner = m.payment_method_owner || m.paymentMethodOwner || m.owner || '';
        const paymentMethodCode = m.payment_method_code || m.paymentMethodCode || '';

        // Keep bank_payment_code as the value for repo settlement_mode (matches existing DB usage).
        if (!bankPaymentCode && !paymentMethod) return null;

        return {
          bankPaymentCode,
          paymentMethod,
          paymentMethodOwner,
          paymentMethodCode
        };
      }).filter(Boolean);

      setSettlementModes(mapped);
    } catch (error) {
      console.error('Error fetching settlement modes:', error);
      setSettlementModes([]);
    } finally {
      setLoadingSettlementModes(false);
    }
  };


  // When multi-select changes, fetch dirty prices for each newly added ISIN
  useEffect(() => {
    const fetchDirtyPrices = async () => {
      const missing = selectedIsins
        .map(s => s.isin)
        .filter(isinNum => dirtyPricesByIsin[isinNum] === undefined);
      if (missing.length === 0) return;
      const entries = await Promise.all(missing.map(async (i) => {
        const dp = await markToMarketApi.getDirtyPrice(i);
        return [i, dp];
      }));
      setDirtyPricesByIsin(prev => {
        const next = { ...prev };
        for (const [k, v] of entries) next[k] = v;
        return next;
      });
    };
    if (selectedIsins.length > 0) {
      fetchDirtyPrices();
    }
  }, [selectedIsins, dirtyPricesByIsin]);

  // State for single ISIN selection before adding
  const [selectedIsinToAdd, setSelectedIsinToAdd] = useState('');

  // Handle adding a single ISIN to the selected list
  const handleAddIsin = () => {
    if (!selectedIsinToAdd) return;
    
    // Check if ISIN is already selected
    const exists = selectedIsins.find(x => x.isin === selectedIsinToAdd);
    if (exists) {
      // Optionally show a message that it's already added
      return;
    }
    
    const lineId =
      typeof crypto !== 'undefined' && crypto.randomUUID
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    
    // Add the new ISIN to the list
    setSelectedIsins([...selectedIsins, { lineId, isin: selectedIsinToAdd, faceValue: '' }]);
    setSelectedIsinToAdd(''); // Reset selection
  };

  const updateLineFaceValue = (idx, value) => {
    const next = [...selectedIsins];
    next[idx] = { ...next[idx], faceValue: value };
    setSelectedIsins(next);
  };

  const removeSelectedLine = (idx) => {
    const next = selectedIsins.filter((_, i) => i !== idx);
    setSelectedIsins(next);
  };

  const resetRepoMode = () => {
    setIsRepoViewMode(false);
    setIsRepoEditMode(false);
    setEditingRepoDealId(null);
    setEditingRepoDealStatus('');
  };

  const resetRepoFormForNextDeal = () => {
    setDealType('Repo');
    setCounterparty('');
    setTradeDate(new Date());
    setValueDate(new Date());
    setMaturityDate(new Date());
    setPrincipalAmount('');
    setRate('');
    setCalculationDayBasis('365');
    setHaircut('');
    setInterestAmount('0.00');
    setMaturityAmount('0.00');
    setTenor(0);
    setFaceValue('');
    setFaceValueAdjustment('');
    setFaceValueAsPerCounterparty('');
    setFundMovement('no');
    setDirtyPricesByIsin({});
    setSelectedIsins([]);
    setSelectedIsinToAdd('');
    setTradeDateHolidayError('');
    setValueDateHolidayError('');
    setCreatedRepoDealId(null);
    setSelectedRepoDocumentDealId(null);
    setShowRepoDocumentSection(false);
    resetRepoMode();
  };

  const toDate = (dateString) => {
    if (!dateString) return new Date();
    const d = new Date(dateString);
    return Number.isNaN(d.getTime()) ? new Date() : d;
  };

  const loadRepoDealIntoForm = async (dealId, mode = 'view') => {
    try {
      setSaveError('');
      const deal = await repoDealsApi.getById(dealId);
      if (!deal) {
        setSaveError('Failed to load deal details.');
        return;
      }

      const isinsFromDeal = Array.isArray(deal.isins) ? deal.isins : [];
      const isinsMapped = isinsFromDeal.map((item, idx) => ({
        lineId: `${deal.id}-${idx}`,
        isin: item.isin,
        faceValue: item.faceValue ?? ''
      }));

      setDealType(deal.deal_type || 'Repo');
      const dealCounterpartyId = deal.counterparty_id ? String(deal.counterparty_id) : '';
      const dealName = String(deal.counterparty_name || '').trim().toLowerCase();
      const dealLongName = String(deal.counterparty_long_name || '').trim().toLowerCase();
      const matchedByName = counterparties.find((cp) => {
        const shortName = String(cp.short_name || '').trim().toLowerCase();
        const longName = String(cp.long_name || '').trim().toLowerCase();
        return (
          (dealName && (shortName === dealName || longName === dealName)) ||
          (dealLongName && (shortName === dealLongName || longName === dealLongName))
        );
      });
      const matchedById = counterparties.find((cp) => String(cp.id || '') === dealCounterpartyId);
      setCounterparty(
        matchedByName
          ? getCounterpartyOptionValue(matchedByName)
          : matchedById
            ? getCounterpartyOptionValue(matchedById)
            : dealCounterpartyId
      );
      setSettlementMode(deal.settlement_mode || '');
      setTradeDate(toDate(deal.trade_date));
      setValueDate(toDate(deal.value_date));
      setMaturityDate(toDate(deal.maturity_date));
      setPrincipalAmount(deal.principal_amount ?? '');
      setInterestAmount(deal.interest_amount ?? '0.00');
      setRate(deal.rate ?? '');
      setMaturityAmount(deal.maturity_amount ?? '0.00');
      setTenor(deal.tenor ?? 0);
      setCalculationDayBasis(String(deal.calculation_day_basis || '365'));
      setHaircut(deal.haircut ?? '');
      setFaceValue(deal.face_value ?? '');
      setFaceValueAdjustment(deal.face_value_adjustment ?? '');
      setFaceValueAsPerCounterparty(deal.face_value_as_per_counterparty ?? '');
      setFundMovement(String(deal.fund_movement || 'no').toLowerCase() === 'yes' ? 'yes' : 'no');
      setSelectedIsins(isinsMapped);
      setSelectedIsinToAdd('');

      setEditingRepoDealId(deal.id);
      setEditingRepoDealStatus(deal.approval_status || deal.current_approval_level || '');
      setIsRepoViewMode(mode === 'view');
      setIsRepoEditMode(mode === 'edit');
      setSaveSuccess(mode === 'edit' ? `Editing rejected repo deal ${deal.deal_number || deal.id}.` : `Viewing repo deal ${deal.deal_number || deal.id}.`);
      setTimeout(() => setSaveSuccess(''), 3000);
    } catch (error) {
      setSaveError(error.response?.data?.message || error.message || 'Failed to load deal details.');
    }
  };

  const handleViewDeal = async (dealId) => {
    await loadRepoDealIntoForm(dealId, 'view');
  };

  const handleEditDeal = async (dealId) => {
    await loadRepoDealIntoForm(dealId, 'edit');
  };

  const handleDocsDeal = (dealId) => {
    setSelectedRepoDocumentDealId(dealId);
    setShowRepoDocumentSection(true);
    setSaveSuccess(`Showing documents for repo deal ${dealId}.`);
    setTimeout(() => setSaveSuccess(''), 2500);
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isRepoViewMode) {
      setSaveError('This deal is in view mode. Use Edit on a rejected deal to make changes.');
      return;
    }

    setSaving(true);
    setSaveError('');
    setSaveSuccess('');

    // A repo is collateralised, so the haircut is mandatory - it is also what the
    // face value is derived from. The backend enforces this too; this check just
    // reports it before the round trip.
    const haircutValue = parseFloat(haircut);
    if (String(haircut ?? '').trim() === '' || !Number.isFinite(haircutValue)) {
      setSaveError('Haircut is required - enter the haircut percentage before saving.');
      setSaving(false);
      return;
    }
    if (haircutValue <= 0) {
      setSaveError('Haircut must be greater than 0% - a repo deal cannot be saved without a haircut.');
      setSaving(false);
      return;
    }
    if (haircutValue >= 100) {
      setSaveError('Haircut must be less than 100%.');
      setSaving(false);
      return;
    }

    // Validate available balance only for Repo transactions (Sherwood pledges
    // its own securities as collateral when borrowing).
    // We check each ISIN's per-line allocation against its own available balance
    // (multiple ISINs can be attached as collateral with independent allocations).
    if (dealType === 'Repo' && selectedIsins.length > 0) {
      const linesWithAllocation = selectedIsins.filter(
        (line) => parseFloat(line.faceValue) > 0
      );

      if (linesWithAllocation.length === 0) {
        setSaveError('Please allocate face value to at least one selected ISIN before saving.');
        setSaving(false);
        return;
      }

      // Validate against FRESH report data, not the snapshot cached at page load.
      // Holdings change as repo/reverse-repo deals mature during the day, so a stale
      // cache could otherwise block a save that is actually valid.
      const freshReportData = await fetchGsecReportData();

      const failures = [];
      for (const line of linesWithAllocation) {
        const allocation = parseFloat(line.faceValue) || 0;
        const result = validateAvailableBalance(
          line.isin,
          allocation,
          dealType,
          freshReportData
        );
        if (!result.isValid) {
          failures.push({ isin: line.isin, ...result });
        }
      }

      if (failures.length > 0) {
        const summary = failures
          .map((f) => `${f.isin}: available ${Number(f.availableBalance || 0).toLocaleString()}, requested ${Number(f.requestedAmount || 0).toLocaleString()}`)
          .join(' | ');
        setSaveError(`Balance validation failed for ${failures.length} ISIN(s): ${summary}`);
        setSaving(false);
        return;
      }

      console.log('Balance validation passed for repo transaction (per-ISIN check)');
    }

    try {
      const repoData = {
        dealType,
        counterparty,
        settlementMode: settlementMode || null,
        tradeDate: tradeDate.toISOString().split('T')[0],
        valueDate: valueDate.toISOString().split('T')[0],
        maturityDate: maturityDate.toISOString().split('T')[0],
        principalAmount: parseFloat(principalAmount),
        interestAmount: parseFloat(interestAmount),
        rate: parseFloat(rate),
        maturityAmount: parseFloat(maturityAmount),
        tenor,
        calculationDayBasis: parseInt(calculationDayBasis),
        isins: selectedIsins.length > 0 
          ? selectedIsins.map(x => ({ isin: x.isin, faceValue: x.faceValue ? parseFloat(x.faceValue) : null }))
          : undefined, // Only send if there are selected ISINs
        issueDate: selectedIsins.length > 0 ? (() => {
          const firstIsin = selectedIsins[0].isin;
          const isinData = isins.find(item => item.isin_number === firstIsin);
          if (isinData && isinData.issue_date) {
            const date = new Date(isinData.issue_date);
            return date.toLocaleDateString('en-GB');
          }
          return '';
        })() : '',
        haircut: parseFloat(haircut) || 0,
        faceValue: parseFloat(faceValue),
        faceValueAdjustment: parseFloat(faceValueAdjustment) || 0,
        faceValueAsPerCounterparty: parseFloat(faceValueAsPerCounterparty) || 0,
        fundMovement
      };

      const userId = getCurrentUserId();
      if (userId) {
        repoData.userId = userId;
      }

      const isRejectedEdit =
        isRepoEditMode &&
        editingRepoDealId &&
        String(editingRepoDealStatus || '').toLowerCase() === 'rejected';

      // Save repo deal to backend
      const response = isRejectedEdit
        ? await repoDealsApi.update(editingRepoDealId, repoData)
        : await axios.post(`${API_BASE_URL}/repo-deals`, repoData, { headers: getAuthHeaders() });

      const dealId = isRejectedEdit
        ? (editingRepoDealId || response?.id)
        : (response.data?.data?.id || response.data?.id || repoData.dealNumber);
      setCreatedRepoDealId(dealId);
      setSelectedRepoDocumentDealId(dealId);
      setShowRepoDocumentSection(true);
      
      const repoLimitWarning = response.data?.limitWarning || response.data?.data?.limitWarning;
      const repoCpLimitWarning =
        response.data?.counterpartyLimitWarning || response.data?.data?.counterpartyLimitWarning;
      setSaveSuccess(
        (isRejectedEdit
          ? 'Rejected repo deal updated and re-submitted for verification successfully.'
          : 'Repo deal saved successfully!') +
          (repoLimitWarning ? ' This deal exceeds your dealer limit — Middle Office has been notified.' : '') +
          (repoCpLimitWarning ? ` ${repoCpLimitWarning.message} Middle Office has been notified.` : '')
      );
      
      // Refresh the blotter to show the new deal
      setBlotterRefreshTrigger(prev => prev + 1);
      // Clear form and switch back to fresh-entry mode for next deal input.
      resetRepoFormForNextDeal();
      
      // Reset form after successful save
      setTimeout(() => {
        setSaveSuccess('');
      }, 3000);
      
    } catch (error) {
      console.error('Error saving repo deal:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to save repo deal. Please try again.';
      setSaveError(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleFormKeyDown = (e) => {
    // Prevent accidental form submit when pressing Enter in inputs/selects.
    // Save should happen explicitly via the Save button.
    if (e.key === 'Enter' && e.target.tagName !== 'TEXTAREA') {
      e.preventDefault();
    }
  };

  return (
    <div className="repo-page" style={{ width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}>
      <div className="repo-header">
        <h1>Repo Reverse Repo Input Screen</h1>
      </div>

      <form onSubmit={handleSubmit} onKeyDown={handleFormKeyDown} className="repo-form" style={{ width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}>
        {/* Transaction Details Section */}
        <div className="form-section">
          <h2>Transaction Details</h2>
          
          <div className="form-grid">
            <div className="form-group">
              <label className="form-label">Deal Type</label>
              <select 
                value={dealType} 
                onChange={(e) => setDealType(e.target.value)}
                className="form-control"
              >
                {dealTypeOptions.map(option => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">Counterparty</label>
              <select 
                value={counterparty} 
                onChange={(e) => setCounterparty(e.target.value)}
                className="form-control"
                disabled={loadingCounterparties}
              >
                <option value="">Select Counterparty</option>
                {(() => {
                  const individual = counterparties.filter(cp => cp.type === 'individual');
                  const joint = counterparties.filter(cp => cp.type === 'joint');
                  const corporate = counterparties.filter(cp => cp.type === 'corporate');
                  return (
                    <>
                      {individual.length > 0 && (
                        <optgroup label="Individual" className="counterparty-individual">
                          {individual.map(cp => (
                            <option key={cp.unique_id || `individual-${cp.id}`} value={getCounterpartyOptionValue(cp)} className="counterparty-individual">
                              {cp.short_name || cp.long_name || `Counterparty ${cp.id}`}
                            </option>
                          ))}
                        </optgroup>
                      )}
                      {joint.length > 0 && (
                        <optgroup label="Joint" className="counterparty-joint">
                          {joint.map(cp => (
                            <option key={cp.unique_id || `joint-${cp.id}`} value={getCounterpartyOptionValue(cp)} className="counterparty-joint">
                              {cp.short_name || cp.long_name || `Counterparty ${cp.id}`}
                            </option>
                          ))}
                        </optgroup>
                      )}
                      {corporate.length > 0 && (
                        <optgroup label="Corporate" className="counterparty-corporate">
                          {corporate.map(cp => (
                           <option key={cp.unique_id || `corporate-${cp.id}`} value={getCounterpartyOptionValue(cp)} className="counterparty-corporate">
                              {cp.short_name || cp.long_name || `Counterparty ${cp.id}`}
                            </option>
                          ))}
                        </optgroup>
                      )}
                    </>
                  );
                })()}
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">Settlement Mode</label>
              <select
                value={settlementMode}
                onChange={(e) => setSettlementMode(e.target.value)}
                className="form-control"
                disabled={loadingSettlementModes}
              >
                <option value="">
                  {loadingSettlementModes ? 'Loading...' : 'Select Settlement Mode'}
                </option>
                {settlementModes.map((m, idx) => {
                  const code = m.bankPaymentCode || '';
                  const method = m.paymentMethod || '';
                  const owner = m.paymentMethodOwner || '';
                  const methodCode = m.paymentMethodCode || '';
                  const value = code || methodCode || method || '';
                  const label = owner && method
                    ? `${owner} - ${method}${methodCode ? ` (${methodCode})` : ''}`
                    : (methodCode || method || code || '');
                  return (
                    <option key={`${value || 'mode'}-${idx}`} value={value}>
                      {label}
                    </option>
                  );
                })}
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">Calculation Day Basis</label>
              <select 
                value={calculationDayBasis} 
                onChange={(e) => setCalculationDayBasis(e.target.value)}
                className="form-control"
              >
                {dayBasisOptions.map(option => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">Fund Movement</label>
              <div style={{ display: 'flex', gap: '16px', alignItems: 'center', minHeight: '40px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <input
                    type="radio"
                    name="fundMovement"
                    value="yes"
                    checked={fundMovement === 'yes'}
                    onChange={(e) => setFundMovement(e.target.value)}
                  />
                  Yes
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <input
                    type="radio"
                    name="fundMovement"
                    value="no"
                    checked={fundMovement === 'no'}
                    onChange={(e) => setFundMovement(e.target.value)}
                  />
                  No
                </label>
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Trade Date (MM/DD/YYYY)</label>
              <DatePicker
                selected={tradeDate}
                onChangeRaw={(e) => e.preventDefault()}
                onChange={date => {
                  setTradeDate(date);
                  if (date) {
                    const formattedDate = date.toISOString().split('T')[0];
                    const currency = 'LKR'; // Default currency for repo deals
                    checkHolidayForCurrency(formattedDate, currency).then(holidayCheck => {
                      if (holidayCheck.isHoliday) {
                        setTradeDateHolidayError(formatHolidayMessage(holidayCheck, 'Trade Date'));
                      } else {
                        setTradeDateHolidayError('');
                      }
                    });
                  } else {
                    setTradeDateHolidayError('');
                  }
                }}
                className={`form-control ${tradeDateHolidayError ? 'is-invalid' : ''}`}
                dateFormat="MM/dd/yyyy"
                minDate={DATE_OBJ_MIN}
                maxDate={DATE_OBJ_MAX}
              />
              {tradeDateHolidayError && (
                <div className="text-danger" style={{ fontSize: '12px', marginTop: '4px' }}>
                  {tradeDateHolidayError}
                </div>
              )}
            </div>

            <div className="form-group">
              <label className="form-label">Value Date (MM/DD/YYYY)</label>
              <DatePicker
                selected={valueDate}
                onChangeRaw={(e) => e.preventDefault()}
                onChange={date => {
                  setValueDate(date);
                  if (date) {
                    const formattedDate = date.toISOString().split('T')[0];
                    const currency = 'LKR'; // Default currency for repo deals
                    checkHolidayForCurrency(formattedDate, currency).then(holidayCheck => {
                      if (holidayCheck.isHoliday) {
                        setValueDateHolidayError(formatHolidayMessage(holidayCheck, 'Value Date'));
                      } else {
                        setValueDateHolidayError('');
                      }
                    });
                  } else {
                    setValueDateHolidayError('');
                  }
                }}
                className={`form-control ${valueDateHolidayError ? 'is-invalid' : ''}`}
                dateFormat="MM/dd/yyyy"
                minDate={DATE_OBJ_MIN}
                maxDate={DATE_OBJ_MAX}
              />
              {valueDateHolidayError && (
                <div className="text-danger" style={{ fontSize: '12px', marginTop: '4px' }}>
                  {valueDateHolidayError}
                </div>
              )}
            </div>

            <div className="form-group">
              <label className="form-label">Maturity Date (MM/DD/YYYY)</label>
              <DatePicker
                selected={maturityDate}
                onChangeRaw={(e) => e.preventDefault()}
                onChange={date => setMaturityDate(date)}
                className="form-control"
                dateFormat="MM/dd/yyyy"
                minDate={DATE_OBJ_MIN}
                maxDate={DATE_OBJ_MAX}
              />
            </div>

            <div className="form-group">
              <label className="form-label">Principal Amount</label>
              <input
                type="number"
                value={principalAmount}
                onChange={(e) => setPrincipalAmount(e.target.value)}
                className="form-control"
                step="0.01"
                min="0"
                placeholder="Enter principal amount"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Rate (%)</label>
              <input
                type="number"
                value={rate}
                onChange={(e) => setRate(e.target.value)}
                className="form-control"
                step="0.01"
                min="0"
                placeholder="Enter rate"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Tenor (Days)</label>
              <input
                type="text"
                value={tenor}
                className="form-control"
                readOnly
                placeholder="Auto calculated"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Interest Amount</label>
              <input
                type="text"
                value={formatAmountWithSeparators(interestAmount)}
                className="form-control"
                readOnly
                placeholder="Auto calculated"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Maturity Amount</label>
              <input
                type="text"
                value={formatAmountWithSeparators(maturityAmount)}
                className="form-control"
                readOnly
                placeholder="Auto calculated"
              />
            </div>
          </div>
        </div>

        {/* Securities Details Section */}
        <div className="form-section">
          <h2>Details of Securities Attached</h2>

          <div className="form-grid">
            <div className="form-group">
              <label className="form-label">Haircut (%) *</label>
              <input
                type="number"
                value={haircut}
                onChange={(e) => setHaircut(e.target.value)}
                className="form-control"
                step="0.01"
                min="0.01"
                max="99.99"
                required
                placeholder="Enter haircut percentage"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Face Value (Auto-calculated)</label>
              <input
                type="text"
                value={formatAmountWithSeparators(faceValue)}
                className="form-control"
                readOnly
                placeholder="Auto-calculated from haircut"
              />
              <small className="form-text text-muted" style={{ fontSize: '11px', marginTop: '4px' }}>
                {(() => {
                  const firstIsin = selectedIsins.length > 0 ? selectedIsins[0].isin : null;
                  const dirtyPrice = firstIsin ? dirtyPricesByIsin[firstIsin] : null;
                  return dirtyPrice ? 
                    'FV = ((MA × 100) ÷ (100 - H)) × 100 ÷ DP' :
                    'FV = (MA × 100) ÷ (100 - H)';
                })()}
              </small>
            </div>

            <div className="form-group">
              <label className="form-label">Face Value Adjustment</label>
              <input
                type="number"
                value={faceValueAdjustment}
                onChange={(e) => setFaceValueAdjustment(e.target.value)}
                className="form-control"
                step="0.01"
                placeholder="Enter adjustment (+/-)"
              />
              <small className="form-text text-muted" style={{ fontSize: '11px', marginTop: '4px' }}>
                Optional: Add or subtract from auto-calculated face value
              </small>
            </div>

            <div className="form-group">
              <label className="form-label">Face Value as per Counterparty</label>
              <input
                type="text"
                value={formatAmountWithSeparators(faceValueAsPerCounterparty)}
                className="form-control"
                readOnly
                placeholder="Auto-calculated with adjustment"
              />
              <small className="form-text text-muted" style={{ fontSize: '11px', marginTop: '4px' }}>
                Final face value after adjustment
              </small>
            </div>
          </div>
        </div>

        {/* ISIN Attachment Section */}
        <div className="form-section">
          <h2>Attach Multiple ISINs (Optional)</h2>
          
          <div className="form-grid" style={{ gridTemplateColumns: '1fr auto', alignItems: 'end' }}>
            <div className="form-group">
              <label className="form-label">Select ISIN</label>
              <select
                className="form-control"
                value={selectedIsinToAdd}
                onChange={(e) => setSelectedIsinToAdd(e.target.value)}
                disabled={loadingIsins}
              >
                <option value="">-- Select an ISIN --</option>
                {isins
                  .filter(item => !selectedIsins.find(s => s.isin === item.isin_number))
                  .map(item => (
                    <option key={item.isin_number} value={item.isin_number}>
                      {item.isin_number} - {item.isin_issuer || 'Security'}
                    </option>
                  ))}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label" style={{ visibility: 'hidden' }}>Add</label>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleAddIsin}
                disabled={!selectedIsinToAdd || loadingIsins}
                style={{ width: '100%', height: '40px', marginTop: '0' }}
              >
                Add
              </button>
            </div>
          </div>

          {selectedIsins.length > 0 && (
            <div className="form-row">
              <div className="form-group" style={{ width: '100%' }}>
                <label>Selected ISIN Lines</label>
                <div style={{ border: '1px solid #ddd', borderRadius: 4, padding: 12 }}>
                  {selectedIsins.map((row, idx) => (
                    <div
                      key={row.lineId ?? `${row.isin}-${idx}`}
                      style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 8 }}
                    >
                      <input
                        type="text"
                        className="form-control"
                        value={row.isin}
                        readOnly
                        style={{ flex: 2 }}
                      />
                      <input
                        type="number"
                        className="form-control"
                        placeholder="Face Value (optional)"
                        value={row.faceValue}
                        onChange={(e) => updateLineFaceValue(idx, e.target.value)}
                        step="0.01"
                        style={{ flex: 1 }}
                      />
                      <input
                        type="text"
                        className="form-control"
                        readOnly
                        value={(parseFloat(row.faceValue) > 0 && dirtyPricesByIsin[row.isin])
                          ? `Valuation: ${formatAmountWithSeparators((((parseFloat(row.faceValue) || 0) / 100) * dirtyPricesByIsin[row.isin]).toFixed(4))}`
                          : 'Valuation: N/A'}
                        style={{ flex: 1.2 }}
                      />
                      <button type="button" className="btn btn-secondary" onClick={() => removeSelectedLine(idx)}>
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
                {(() => {
                  const required = parseFloat(faceValue) || 0; // Face Value (auto-calculated)
                  const allocated = selectedIsins.reduce((sum, r) => sum + (parseFloat(r.faceValue) || 0), 0);
                  const remaining = Math.max(0, required - allocated);
                  return (
                    <div style={{ marginTop: 8, display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
                      <span className="form-text text-muted">Required Face Value: {formatAmountWithSeparators(required.toFixed ? required.toFixed(2) : required)}</span>
                      <span className="form-text text-muted">Allocated: {formatAmountWithSeparators(allocated.toFixed(2))}</span>
                      <span className={remaining > 0 ? 'text-warning' : 'text-success'}>
                        Remaining to allocate: {formatAmountWithSeparators(remaining.toFixed(2))}
                      </span>
                    </div>
                  );
                })()}
                <small className="form-text text-muted">Valuation per line = (Face Value ÷ 100) × Dirty Price</small>
              </div>
            </div>
          )}
        </div>

        {/* Form Actions */}
        <div className="form-actions">
          <button 
            type="submit" 
            className="btn btn-primary"
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save Repo Deal'}
          </button>
          
          <button 
            type="button" 
            className="btn btn-secondary"
            onClick={() => {
              resetRepoFormForNextDeal();
            }}
          >
            Reset Form
          </button>
        </div>

        {/* Status Messages */}
        {saveError && (
          <div className="alert alert-danger">
            {saveError}
          </div>
        )}
        
        {saveSuccess && (
          <div className="alert alert-success">
            {saveSuccess}
          </div>
        )}
        
        {/* Balance Validation Display */}
        {!balanceValidation.isValid && (
          <div className="alert alert-warning">
            <strong>Balance Validation:</strong> {balanceValidation.message}
          </div>
        )}
        
        {balanceValidation.isValid && balanceValidation.message && (
          <div className="alert alert-info">
            <strong>Balance Status:</strong> {balanceValidation.message}
          </div>
        )}
      </form>

      {/* Document Upload Section - Show after deal is created */}
      {showRepoDocumentSection && (selectedRepoDocumentDealId || createdRepoDealId) && (
        <div style={{ marginTop: '24px', width: '100%' }}>
          <DocumentUpload
            transactionType="repo"
            transactionId={selectedRepoDocumentDealId || createdRepoDealId}
            onUploadSuccess={(documents) => {
              setSaveSuccess('Document(s) uploaded successfully');
              setShowRepoDocumentSection(false);
            }}
            onUploadError={(error) => {
              setSaveError('Failed to upload document: ' + (error.response?.data?.error || error.message));
            }}
            multiple={true}
          />
          <DocumentList
            transactionType="repo"
            transactionId={selectedRepoDocumentDealId || createdRepoDealId}
            onDocumentDeleted={() => {}}
          />
        </div>
      )}

      {/* Recent Repo Deals Blotter */}
      <RepoBlotter
        refreshTrigger={blotterRefreshTrigger}
        onViewDeal={handleViewDeal}
        onEditDeal={handleEditDeal}
        onDocsDeal={handleDocsDeal}
      />
    </div>
  );
};

export default RepoPage;